import { styled } from '@stitches/configed';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  Input,
  message,
  Row,
  Switch,
} from 'antd';
import { useRef, useState, useEffect } from 'react';
import moment from 'moment';
import BusinessHours from './BussinessHours';
import { DayGroup, SellingScheduleResponse, Weekday } from '../data';
import { sellingScheduleService } from '../service';
import { useAppSelector } from '../../../../store';
import { useNavigate } from 'react-router-dom';

type Props = {
  form: FormInstance<any>;
  schedule: SellingScheduleResponse;
};

const CardHeading = styled('h1', {
  marginBottom: '24px',
  fontFamily: 'YOUGO',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
});

const CustomDivider = styled(Divider, {
  '& > span': {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0px',
    padding: '4px 6px !important',
    backgroundColor: '#E8EAED',
    borderRadius: '6px',
    margin: '0',
    marginRight: '10px',
  },
});

const CustomSwitch = styled(Switch, {
  '&.ant-switch-checked': {
    backgroundColor: '#17AFA7 !important',
  },
});

const weekdaysEnum = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  'Chủ Nhật': 0,
  'Thứ Hai': 1,
  'Thứ Ba': 2,
  'Thứ Tư': 3,
  'Thứ Năm': 4,
  'Thứ Sáu': 5,
  'Thứ Bảy': 6,
} as const;

const DAYS_ENUM_MAP = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];

const formatTime = (time: string) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minutes} ${ampm}`;
};

export default function EditScheduleForm({
  form,
  schedule,
}: Props) {
  const navigate = useNavigate();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const restaurantId = currentRestaurant?.id;

  const formRef = useRef(null);
  const [businessHours, setBusinessHours] = useState<DayGroup[]>([]);
  const [isOpenBaseHourOperation, setIsOpenBaseHourOperation] = useState(schedule.isOpenBaseHourOperation);
  const [validateBusinessHours, setValidateBusinessHours] = useState(false);

  const transformBlocksToBusinessHours = (blocks: any[]) => {
    return blocks.map((block, index) => {
      const sortedWeekdays = [...block.weekdays].sort((a, b) => a - b).map((day: any) => DAYS_ENUM_MAP[day]);

      const timeSlots = block.workTime
        .sort((a: any, b: any) => a.order - b.order)
        .map((workTime: any) => ({
          start: workTime.isStartWorkTime ? 'open' : formatTime(workTime.start),
          end: workTime.isEndWorkTime ? 'close' : formatTime(workTime.end),
          isStartWorkTime: workTime.isStartWorkTime,
          isEndWorkTime: workTime.isEndWorkTime,
          order: workTime.order
        }));

      return {
        id: `group-${Date.now() + index}`,
        selectedDays: sortedWeekdays,
        weekdays: sortedWeekdays,
        timeSlots,
        block: index // Use block.order as the block identifier
      };
    });
  };

  useEffect(() => {
    if (schedule?.blocks) {
      setBusinessHours(transformBlocksToBusinessHours(schedule.blocks));
    }
  }, [schedule]);

  const handleSubmit = async () => {
    try {
      setValidateBusinessHours(true);
      const values = await form.validateFields();

      // Validate business hours
      if (businessHours.length === 0) {
        console.error("Vui lòng chọn ít nhất một ngày");
      }

      const hasValidTimeSlots = businessHours.every(group => {
        if (group.selectedDays.length === 0) return false;
        return group.timeSlots.some(slot =>
          (slot.start !== null || slot.isStartWorkTime) &&
          (slot.end !== null || slot.isEndWorkTime)
        );
      });

      if (!hasValidTimeSlots) {
        console.error("Mỗi ngày phải có ít nhất một khung giờ hợp lệ");
      }

      console.log({ businessHours });
      const formattedData = {
        name: values.name,
        isOpenBaseHourOperation: isOpenBaseHourOperation,
        workTimes: businessHours.map(group => ({
          ...group,
          timeSlots: group.timeSlots.map(slot => ({
            ...slot,
            start: slot.start === 'open' ? 'open' : (slot.start?.replace(/AM|PM/g, '').trim() || null),
            end: slot.end === 'close' ? 'close' : (slot.end?.replace(/AM|PM/g, '').trim() || null),
            isStartWorkTime: slot.isStartWorkTime,
            isEndWorkTime: slot.isEndWorkTime
          })),
          weekdays: group.weekdays.map(day => weekdaysEnum[day as keyof typeof weekdaysEnum]),
          block: group.block
        })),
        fromDate: values.fromDate?.toISOString(),
        toDate: values.toDate?.toISOString()
      };

      console.log({ formattedData });

      sellingScheduleService.update(restaurantId, schedule.id, formattedData).then((res) => {
        if (res.data && res.data.message === 'success') {
          message.success('Thành công');
          navigate('/menus/selling-schedule');
          return;
        }
      }).catch((err) => {
        message.error(err.response.data.message || 'Đã xảy ra lỗi, vui lòng kiểm tra lại!');
        console.log("err", err.response.data);
      });
    } catch (error) {
      message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
      console.error("Form validation failed:", error);
    }
  };

  const handleBaseHourOperationChange = (checked: boolean) => {
    setIsOpenBaseHourOperation(checked);
  };

  return (
    <Form
      ref={formRef}
      form={form}
      layout="vertical"
      initialValues={{
        name: schedule.name,
        isOpenBaseHourOperation: schedule.isOpenBaseHourOperation,
        fromDate: schedule.fromDate ? moment(schedule.fromDate) : null,
        toDate: schedule.toDate ? moment(schedule.toDate) : null,
      }}
      scrollToFirstError={{
        behavior: (actions) =>
          actions.forEach(({ el, top, left }) => {
            el.scrollTop = top - 150;
            el.scrollLeft = left;
          }),
      }}
    >
      <Card>
        <CardHeading>Lịch bán</CardHeading>
        <Form.Item name="name" label="Tên gợi nhớ lịch bán" rules={[{ required: true }]}>
          <Input placeholder="VD: Lịch bán sáng" />
        </Form.Item>

        <CustomDivider orientation="left" orientationMargin={0}>
          Khung giờ mở bán
        </CustomDivider>

        <BusinessHours onChange={setBusinessHours} validate={validateBusinessHours} businessHours={businessHours} />

        <CustomDivider orientation="left" orientationMargin={0}>
          Ngày mở bán (Không bắt buộc)
        </CustomDivider>

        <Form.Item name="isOpenBaseHourOperation">
          <div style={{ display: 'flex', gap: 10 }}>
            <CustomSwitch onChange={handleBaseHourOperationChange} checked={isOpenBaseHourOperation} style={{ backgroundColor: '#CF4C22' }}
            />
            <p>Mở bán theo ngày hoạt động của quán</p>
          </div>
        </Form.Item>

        {!isOpenBaseHourOperation && (
          <Card>
            <CardHeading>Chọn khoảng ngày mở bán</CardHeading>
            <Row
              gutter={[16, 16]}
              style={{ marginTop: 8 }}
              align="middle"
            >
              <Col span={11}>
                <Form.Item style={{ marginBottom: 0 }} name="fromDate" rules={[{ required: true, message: 'Vui lòng chọn ngày bắt đầu' }]}>
                  <DatePicker placeholder="dd/mm/yyyy" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={1} style={{ textAlign: "center" }}>
                -
              </Col>
              <Col span={10}>
                <Form.Item style={{ marginBottom: 0 }} name="toDate" rules={[{ required: true, message: 'Vui lòng chọn ngày kết thúc' }]}>
                  <DatePicker placeholder="dd/mm/yyyy" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        <Row justify="end" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Button type="ghost" onClick={() => window.history.back()}>Đóng</Button>

          <Button
            type="primary"
            onClick={handleSubmit}
          >
            Lưu
          </Button>
        </Row>
      </Card>

    </Form>
  );
}
